import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  CloseOutlined,
  ExperimentOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Button,
  Input,
  Modal,
  Spin,
} from "antd";
import { toast } from "react-toastify";

import StyledScrollbar from "../../../Common/UIComponents/StyledScrollbar";
import { TEST_TIERS } from "../constants";

import {
  buildRemainingCreditsByCode,
  buildTestFilters,
  mapUsageCategoryTestsToModal,
} from "../utils";

import { fetchUsageCategoryTests } from "../../../../actions/usage";

import "./UsageMetricsTestsModal.css";

const CATEGORY_ICONS = {
  microbiology: <ExperimentOutlined />,
  chemistry: <ExperimentOutlined />,
};

const formatCreditCount = (value) => (
  Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })
);

export default function UsageMetricsTestsModal({
  open,
  onClose,
  categoryUsage,
  billingMode,
  topupPlan = false,
}) {
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setActiveFilter("All");
      return undefined;
    }

    const controller = new AbortController();
    let isMounted = true;

    const loadTests = async () => {
      setLoading(true);
      const response = await fetchUsageCategoryTests({
        status: 1,
        signal: controller.signal,
      });

      if (!isMounted || response.canceled) return;

      if (!response.success) {
        toast.error(response.message);
        setLoading(false);
        return;
      }

      const remainingByCode = buildRemainingCreditsByCode(categoryUsage, { billingMode, topupPlan });
      setCategories(
        mapUsageCategoryTestsToModal(response.categories, remainingByCode),
      );
      setLoading(false);
    };

    loadTests();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [open, categoryUsage, billingMode, topupPlan]);

  const filters = useMemo(() => buildTestFilters(categories), [categories]);

  const visibleCategories = useMemo(() => {
    const query = search.trim().toLowerCase();

    return categories.map((category) => ({
      ...category,
      tests: category.tests.filter((test) => (
        (activeFilter === "All" || test.tier === activeFilter)
        && (!query || test.name.toLowerCase().includes(query))
      )),
    })).filter((category) => category.tests.length > 0);
  }, [activeFilter, categories, search]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={920}
      centered
      destroyOnHidden
      className="UsageMetricsTestsModal"
    >
      <header className="UsageMetricsTestsModal__Header">
        <h2>Tests in category</h2>
        <Button
          type="text"
          icon={<CloseOutlined />}
          className="UsageMetricsTestsModal__CloseButton"
          onClick={onClose}
          aria-label="Close tests modal"
        />
      </header>

      <div className="UsageMetricsTestsModal__Controls">
        <div className="UsageMetricsTestsModal__Filters">
          {filters.map((filter, index) => (
            <React.Fragment key={filter.key}>
              {index === 1 && (
                <span className="UsageMetricsTestsModal__FilterDivider" aria-hidden />
              )}
              <button
                type="button"
                onClick={() => setActiveFilter(filter.key)}
                className={`UsageMetricsTestsModal__Filter${
                  activeFilter === filter.key ? " UsageMetricsTestsModal__Filter--active" : ""
                }`}
              >
                {filter.label}
              </button>
            </React.Fragment>
          ))}
        </div>
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          prefix={<SearchOutlined />}
          placeholder="Search tests..."
          allowClear
          className="UsageMetricsTestsModal__Search"
        />
      </div>

      {loading ? (
        <div className="UsageMetricsTestsModal__Loading">
          <Spin size="large" />
        </div>
      ) : (
        <StyledScrollbar
          className="UsageMetricsTestsModal__Scroll"
          noScrollX
          autoHide
          disableTrackYWidthCompensation
          disableTracksWidthCompensation
        >
          <div className="UsageMetricsTestsModal__Categories">
            {visibleCategories.map((category) => (
              <section className="UsageMetricsTestsModal__Category" key={category.key}>
                <h3 className="UsageMetricsTestsModal__CategoryTitle">
                  <span className={`UsageMetricsTestsModal__CategoryIcon ${category.iconClass}`}>
                    {CATEGORY_ICONS[category.key]}
                  </span>
                  {category.name}
                </h3>

                <div className="UsageMetricsTestsModal__Tiers">
                  {TEST_TIERS.map((tier) => {
                    const tests = category.tests.filter((test) => test.tier === tier);
                    if (!tests.length) return null;

                    const tierTotal = category.tierTotals?.[tier];
                    const amountUnit = Number(tierTotal) === 1 ? "test" : "tests";

                    return (
                      <div className="UsageMetricsTestsModal__Tier" key={tier}>
                        <h4>{`${tier} (${formatCreditCount(tierTotal)} ${amountUnit})`}</h4>
                        <StyledScrollbar
                          className="UsageMetricsTestsModal__TierScroll"
                          noScrollX
                          autoHide
                          disableTrackYWidthCompensation
                          disableTracksWidthCompensation
                        >
                          <ul>
                            {tests.map((test) => (
                              <li key={test.id}>
                                <span>{test.name}</span>
                                {test.used != null && (
                                  <span className="UsageMetricsTestsModal__Used">
                                    {`(${test.used} used)`}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </StyledScrollbar>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}

            {!visibleCategories.length && (
              <p className="UsageMetricsTestsModal__Empty">No tests found.</p>
            )}
          </div>
        </StyledScrollbar>
      )}
    </Modal>
  );
}
